import React, { useState, useEffect } from 'react';
import './PMCalibrationReports.css';

const PMCalibrationReports = ({ currentUser }) => {
  const [pmCalibrations, setPmCalibrations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Filter states
  const [filterEquipment, setFilterEquipment] = useState('');
  const [filterReportType, setFilterReportType] = useState('all');
  const [filterSerialNumber, setFilterSerialNumber] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssetId, setFilterAssetId] = useState('');

  useEffect(() => {
    loadPmCalibrations();
    loadAssets();
  }, []);

  const parseApiCollection = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.eye-main-btn') && !e.target.closest('.report-action-dropdown')) {
        document.querySelectorAll('.report-action-dropdown').forEach(d => {
          d.style.display = 'none';
        });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadPmCalibrations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/assets');
      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }

      const payload = await response.json();
      const apiAssets = parseApiCollection(payload);

      const records = apiAssets.filter(asset => {
        if (!asset || typeof asset !== 'object') return false;
        const status = asset.status || 'active';
        return status !== 'condemned' && status !== 'shifted';
      }).map(asset => ({
        id: asset.id || asset.assetId,
        assetId: asset.assetId,
        equipmentName: asset.equipmentName,
        serialNumber: asset.serialNo,
        // PM data
        pmDueDate: asset.pmDue,
        pmDoneDate: asset.pmDone,
        pmStatus: getPMStatus(asset.pmDue, asset.pmDone),
        pmVendor: asset.pmVendor,
        pmCost: asset.pmCost,
        pmNotes: asset.pmNotes,
        // Calibration data
        calibrationDueDate: asset.calDue,
        calibrationDoneDate: asset.calDone,
        calibrationStatus: getCalStatus(asset.calDue, asset.calDone),
        calibrationVendor: asset.calibrationVendor,
        calibrationCost: asset.calibrationCost,
        calibrationNotes: asset.calibrationNotes,
        // Report data
        pmReportFileName: asset.pmReportFileName,
        pmReportUploadDate: asset.pmReportUploadDate,
        pmReportData: asset.pmReportData,
        calReportFileName: asset.calReportFileName,
        calReportUploadDate: asset.calReportUploadDate,
        calReportData: asset.calReportData,
        // Full report history arrays (version tracking)
        pmReportHistory: asset.pmReportHistory || [],
        calReportHistory: asset.calReportHistory || []
      }));
      setPmCalibrations(records);
    } catch (error) {
      console.error('Error loading PM/Calibrations:', error);
      showMessage('Failed to load PM/Calibration records', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await fetch('/api/assets');
      if (!response.ok) {
        throw new Error(`Failed to load assets (${response.status})`);
      }

      const payload = await response.json();
      setAssets(parseApiCollection(payload));
    } catch (error) {
      console.error('Error loading assets:', error);
    }
  };

  // Helper functions to determine status
  const getPMStatus = (pmDue, pmDone) => {
    if (pmDone) return 'Completed';
    if (!pmDue) return 'No Data';
    const dueDate = new Date(pmDue);
    const today = new Date();
    if (dueDate < today) return 'Overdue';
    return 'Pending';
  };

  const getCalStatus = (calDue, calDone) => {
    if (calDone) return 'Completed';
    if (!calDue) return 'No Data';
    const dueDate = new Date(calDue);
    const today = new Date();
    if (dueDate < today) return 'Overdue';
    return 'Pending';
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const getReportFileName = (report) => report.pmReportFileName || report.calReportFileName || '';

  // Support stored report formats: direct URL/data URL, base64 string, JSON array/object wrappers.
  const normalizeReportPayload = (rawReportData, fallbackFileName = '') => {
    if (rawReportData === null || rawReportData === undefined) return null;

    if (Array.isArray(rawReportData)) {
      for (const item of rawReportData) {
        const normalized = normalizeReportPayload(item, fallbackFileName);
        if (normalized) return normalized;
      }
      return null;
    }

    if (typeof rawReportData === 'object') {
      const nested = rawReportData.data || rawReportData.reportData || rawReportData.url || rawReportData.src;
      return normalizeReportPayload(nested, fallbackFileName);
    }

    if (typeof rawReportData !== 'string') return null;

    const trimmedData = rawReportData.trim();
    if (!trimmedData || trimmedData === '[STORAGE_LIMITED]') return null;

    if (trimmedData.startsWith('[') || trimmedData.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmedData);
        const normalized = normalizeReportPayload(parsed, fallbackFileName);
        if (normalized) return normalized;
      } catch (e) {
        // Not JSON, continue with string-based checks.
      }
    }

    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(trimmedData) && trimmedData.length > 100;
    if (isBase64) {
      const ext = fallbackFileName.split('.').pop()?.toLowerCase();
      let mimeType = 'image/jpeg';
      if (ext === 'png') mimeType = 'image/png';
      else if (ext === 'gif') mimeType = 'image/gif';
      else if (ext === 'pdf') mimeType = 'application/pdf';
      else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
      return `data:${mimeType};base64,${trimmedData}`;
    }

    return trimmedData;
  };

  // Get asset details by assetId
  const getAssetDetails = (assetId) => {
    return assets.find(asset => asset.assetId === assetId) || {};
  };

  // Generate expected report filename based on PM/Calibration record
  // Format: EquipmentName_AssetID_SN_Number_CAL_DDMMYYYY.ext
  const generateReportFilename = (record, type) => {
    const asset = getAssetDetails(record.assetId);
    const equipmentName = (asset.equipmentName || 'Unknown').replace(/\s+/g, '_');
    const assetId = record.assetId || 'NOASSET';
    const serialNumber = asset.serialNo || 'NOSN';
    const date = type === 'pm' ? (record.pmDoneDate || record.lastPmDate) : (record.calibrationDoneDate || record.lastCalibrationDate);
    // Format date as DDMMYYYY
    let formattedDate = '';
    if (date) {
      // Handle different date formats
      let dateStr = date;
      if (date instanceof Date) {
        // If it's a Date object, format it
        dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      } else if (typeof date !== 'string') {
        // Convert to string if it's not already
        dateStr = String(date);
      }
      const dateParts = dateStr.split('-');
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}${dateParts[1]}${dateParts[0]}`;
      } else {
        formattedDate = dateStr.replace(/-/g, '');
      }
    } else {
      const now = new Date();
      formattedDate = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
    }
    const typePrefix = type === 'pm' ? 'PM' : 'CAL';
    return `${equipmentName}_${assetId}_${serialNumber}_${typePrefix}_${formattedDate}.pdf`;
  };

  // Get equipment name from asset
  const getEquipmentName = (assetId) => {
    const asset = getAssetDetails(assetId);
    return asset.equipmentName || 'Unknown Equipment';
  };

  // Get serial number from asset
  const getSerialNumber = (assetId) => {
    const asset = getAssetDetails(assetId);
    return asset.serialNo || assetId || 'N/A';
  };

  // Format date for display (convert to DD-Mon-YYYY format consistently)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    
    // Handle Date objects - use UTC methods for cross-browser consistency
    if (dateStr instanceof Date) {
      const day = String(dateStr.getUTCDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[dateStr.getUTCMonth()];
      const year = dateStr.getUTCFullYear();
      return `${day}-${month}-${year}`;
    }
    
    // Handle numeric values (Excel serial dates or timestamps)
    if (typeof dateStr === 'number' || (typeof dateStr === 'string' && /^\d+$/.test(dateStr.trim()))) {
      const numValue = parseInt(dateStr, 10);
      
      // Check if it's an Excel serial date (typically 40000-50000 range for 2009-2040)
      // Excel epoch starts at 1900-01-01 (with the 1900 leap year bug)
      if (numValue > 30000 && numValue < 60000) {
        // Excel date: days since 1900-01-01, but need to adjust for Excel's 1900 leap year bug
        const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899 (Excel's epoch with bug correction)
        const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      
      // Check if it's a Unix timestamp (in milliseconds, typically 13 digits)
      if (numValue > 1000000000000) {
        const date = new Date(numValue);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${day}-${month}-${year}`;
      }
      
      // Check if it's a Unix timestamp (in seconds, typically 10 digits)
      if (numValue > 1000000000 && numValue < 10000000000) {
        const date = new Date(numValue * 1000);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${day}-${month}-${year}`;
      }
    }
    
    // Handle string dates
    if (typeof dateStr === 'string') {
      const cleanedDate = dateStr.trim();
      
      // Check if already in DD-Mon-YYYY format (Asset Date format)
      if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(cleanedDate)) {
        return cleanedDate;
      }
      
      // Check if in DD-MM-YYYY format and convert to DD-Mon-YYYY
      if (/^\d{2}-\d{2}-\d{4}$/.test(cleanedDate)) {
        const parts = cleanedDate.split('-');
        const day = parts[0];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[parseInt(parts[1], 10) - 1];
        const year = parts[2];
        return `${day}-${month}-${year}`;
      }
      
      // Check if in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleanedDate)) {
        const parts = cleanedDate.split('-');
        const day = parts[2];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[parseInt(parts[1], 10) - 1];
        const year = parts[0];
        return `${day}-${month}-${year}`;
      }
      
      // For ambiguous date strings, try to parse manually first
      // Handle formats like "Mon Jan 01 2024" or other browser-specific formats
      const isoMatch = cleanedDate.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const year = isoMatch[1];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[parseInt(isoMatch[2], 10) - 1];
        const day = isoMatch[3];
        return `${day}-${month}-${year}`;
      }
      
      // Try to parse as date - use UTC to avoid timezone issues
      const date = new Date(cleanedDate);
      if (!isNaN(date.getTime())) {
        // Use UTC methods to ensure consistent formatting across browsers
        const day = String(date.getUTCDate()).padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${day}-${month}-${year}`;
      }
    }
    
    return String(dateStr);
  };

  // Transform PM/Calibration records to report format
  const transformToReports = () => {
    const reports = [];
    
    pmCalibrations.forEach(record => {
      const equipmentName = getEquipmentName(record.assetId);
      const serialNumber = getSerialNumber(record.assetId);
      
      // Add PM report entry if PM data exists
      if (record.pmDoneDate || record.lastPmDate || record.pmDueDate) {
        reports.push({
          id: `${record.id}_pm`,
          recordId: record.id,
          equipmentName: equipmentName,
          serialNumber: serialNumber,
          assetId: record.assetId,
          reportType: 'pm',
          doneDate: record.pmDoneDate || record.lastPmDate || '',
          dueDate: record.pmDueDate || '',
          status: record.pmStatus || 'Unknown',
          fileName: generateReportFilename(record, 'pm'),
          vendor: record.pmVendor || record.vendorName || '',
          cost: record.pmCost || '',
          notes: record.pmNotes || record.notes || '',
          pmReportData: record.pmReportData,
          pmReportFileName: record.pmReportFileName,
          pmReportUploadDate: record.pmReportUploadDate
        });
      }
      
      // Add Calibration report entry if Calibration data exists
      if (record.calibrationDoneDate || record.lastCalibrationDate || record.calibrationDueDate) {
        reports.push({
          id: `${record.id}_cal`,
          recordId: record.id,
          equipmentName: equipmentName,
          serialNumber: serialNumber,
          assetId: record.assetId,
          reportType: 'calibration',
          doneDate: record.calibrationDoneDate || record.lastCalibrationDate || '',
          dueDate: record.calibrationDueDate || '',
          status: record.calibrationStatus || 'Unknown',
          fileName: generateReportFilename(record, 'calibration'),
          vendor: record.calibrationVendor || record.vendorName || '',
          cost: record.calibrationCost || '',
          notes: record.calibrationNotes || record.notes || '',
          calReportData: record.calReportData,
          calReportFileName: record.calReportFileName,
          calReportUploadDate: record.calReportUploadDate
        });
      }
    });
    
    return reports;
  };

  // Filter reports
  const allReports = transformToReports();
  const safeLower = (value) => String(value ?? '').toLowerCase();

  const filteredReports = allReports.filter((report) => {
    let matches = true;

    if (filterEquipment && !safeLower(report.equipmentName).includes(safeLower(filterEquipment))) {
      matches = false;
    }

    if (filterReportType !== 'all' && report.reportType !== filterReportType) {
      matches = false;
    }

    if (filterSerialNumber && !safeLower(report.serialNumber).includes(safeLower(filterSerialNumber))) {
      matches = false;
    }

    if (filterStatus !== 'all' && !safeLower(report.status).includes(safeLower(filterStatus))) {
      matches = false;
    }

    if (filterAssetId && !safeLower(report.assetId).includes(safeLower(filterAssetId))) {
      matches = false;
    }

    return matches;
  });

  // Get unique equipment names for filter
  const uniqueEquipmentNames = [...new Set(allReports.map(r => r.equipmentName).filter(Boolean))].sort();

  // Handle download - this would link to actual file if exists
  const handleDownload = (report) => {
    // In a real implementation, this would check if file exists and download it
    // For now, show a message that this is the expected filename
    showMessage(`Expected file: ${report.fileName}`, 'info');
  };

  // Handle upload report file for a specific record
  const handleUploadReport = async (report, file) => {
    if (!file) return;

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      showMessage('File size exceeds 50MB limit', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('equipmentName', report.equipmentName);
    formData.append('serialNumber', report.serialNumber);
    formData.append('assetId', report.assetId);
    formData.append('doneDate', report.doneDate);
    formData.append('reportType', report.reportType);
    formData.append('recordId', report.recordId);

    try {
      const response = await fetch('/api/pm-calibration-reports/upload', {
        method: 'POST',
        headers: {
          'user-name': currentUser?.name || 'Unknown'
        },
        body: formData
      });

      if (response.ok) {
        showMessage('Report uploaded successfully!', 'success');
        loadPmCalibrations(); // Reload to refresh data
      } else {
        const error = await response.json();
        showMessage(error.message || 'Failed to upload report', 'error');
      }
    } catch (error) {
      console.error('Error uploading report:', error);
      showMessage('Failed to upload report', 'error');
    }
  };

  // Handle print report - opens print dialog directly
  const handlePrintReport = (report) => {
    const rawReportData = report.reportType === 'pm' ? report.pmReportData : report.calReportData;
    const reportData = normalizeReportPayload(rawReportData, getReportFileName(report));
    if (!reportData) {
      showMessage(rawReportData ? 'Report preview is unavailable due to invalid or storage-limited data.' : 'No report uploaded yet', 'error');
      return;
    }
    
    // Check if it's a valid URL (data URL, http/https URL, relative URL, or blob URL)
    const isDataUrl = reportData.startsWith('data:');
    const isHttpUrl = reportData.startsWith('http://') || reportData.startsWith('https://');
    const isRelativeUrl = reportData.startsWith('/') || reportData.startsWith('./');
    const isBlobUrl = reportData.startsWith('blob:');
    
    if (!isDataUrl && !isHttpUrl && !isRelativeUrl && !isBlobUrl) {
      showMessage('Invalid report format. Cannot print this file.', 'error');
      return;
    }
    
    // Create a blob URL from the report data for better compatibility
    let fileUrl = reportData;
    let fileMimeType = '';
    if (isDataUrl) {
      // Convert data URL to blob URL for better browser compatibility
      try {
        const byteString = atob(reportData.split(',')[1]);
        const mimeString = reportData.split(',')[0].split(':')[1].split(';')[0];
        fileMimeType = mimeString;
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        fileUrl = URL.createObjectURL(blob);
      } catch (e) {
        console.error('Error converting data URL to blob:', e);
        fileUrl = reportData; // Fallback to original data URL
      }
    }
    
    // Detect if file is PDF
    const isPdf = fileMimeType.includes('pdf') || 
                  reportData.toLowerCase().includes('pdf') ||
                  (report.pmReportFileName || report.calReportFileName || '').toLowerCase().endsWith('.pdf');
    
    // Generate proper file name
    const equipmentName = (report.equipmentName || 'Equipment').replace(/\s+/g, '_');
    const assetId = report.assetId || 'Unknown';
    const serialNumber = report.serialNumber || 'SN_Unknown';
    const reportType = report.reportType === 'pm' ? 'PM' : 'CAL';
    const doneDate = report.doneDate || '';
    
    // Format date as DDMMYYYY
    let dateStr = '';
    // Convert to string if it's a Date object or other type
    const doneDateStr = doneDate ? String(doneDate) : '';
    if (doneDateStr && doneDateStr !== 'NA' && doneDateStr !== 'NR') {
      const dateMatch = doneDateStr.match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);
      if (dateMatch) {
        const day = dateMatch[1];
        const monthMap = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
          'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
          'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        const month = monthMap[dateMatch[2]] || '01';
        const year = dateMatch[3];
        dateStr = `${day}${month}${year}`;
      }
    }
    
    const fileExtension = isPdf ? 'pdf' : 'jpg';
    const fileName = dateStr 
      ? `${equipmentName}_${assetId}_${serialNumber}_${reportType}_${dateStr}.${fileExtension}`
      : `${equipmentName}_${assetId}_${serialNumber}_${reportType}.${fileExtension}`;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <meta charset="utf-8">
            <title>Print Report - ${report.equipmentName}</title>
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; padding: 20px; font-family: 'Segoe UI', Arial, sans-serif; background: white; }
              .header { text-align: center; margin-bottom: 20px; padding-bottom: 20px; border-bottom: 2px solid #1a3c6c; }
              .header h1 { margin: 0; font-size: 20px; color: #1a3c6c; }
              .header p { margin: 5px 0 0 0; color: #64748b; font-size: 14px; }
              .image-container { text-align: center; padding: 20px; }
              .image-container img { max-width: 100%; max-height: 800px; border: 1px solid #e0e0e0; }
              .pdf-container { width: 100%; height: 800px; border: 1px solid #e0e0e0; }
              .info { margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px; }
              .info-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 13px; }
              .info-label { font-weight: 600; color: #64748b; }
              .info-value { color: #1e293b; }
              @media print {
                body { padding: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${report.reportType === 'pm' ? 'PM Report' : 'Calibration Report'}</h1>
              <p>${report.equipmentName} | Asset ID: ${assetId} | Serial: ${serialNumber}</p>
            </div>
            <div class="image-container">
              ${isPdf ? 
                `<embed src="${fileUrl}" type="application/pdf" class="pdf-container" />` :
                `<img src="${fileUrl}" alt="Report" onload="console.log('Print image loaded')" onerror="console.error('Print image failed'); this.src='${reportData}';" />`
              }
            </div>
            <div class="info">
              <div class="info-row">
                <span class="info-label">Equipment:</span>
                <span class="info-value">${report.equipmentName}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Asset ID:</span>
                <span class="info-value">${assetId}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Serial Number:</span>
                <span class="info-value">${serialNumber}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Done Date:</span>
                <span class="info-value">${report.doneDate || 'N/A'}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Due Date:</span>
                <span class="info-value">${report.dueDate || 'N/A'}</span>
              </div>
            </div>
            <div class="no-print" style="text-align: center; margin-top: 20px;">
              <button onclick="triggerPrint()" style="padding: 10px 20px; background: #4361ee; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;">&#x1F5A8;&#xFE0F; Print
              </button>
            </div>
            <script>
              function triggerPrint() {
                try {
                  window.focus();
                  window.print();
                } catch (_error) {
                  window.print();
                }
              }

              window.onload = function() {
                window.focus();
                window.print();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      showMessage('Print dialog opened', 'success');
    } else {
      showMessage('Popup blocked! Please allow popups for this site.', 'error');
    }
  };

  // Handle save/download report - downloads the report directly
  const handleSaveReport = (report) => {
    const rawReportData = report.reportType === 'pm' ? report.pmReportData : report.calReportData;
    const reportData = normalizeReportPayload(rawReportData, getReportFileName(report));
    if (!reportData) {
      showMessage(rawReportData ? 'Report preview is unavailable due to invalid or storage-limited data.' : 'No report uploaded yet', 'error');
      return;
    }
    
    // Validate report data format
    if (typeof reportData !== 'string' || reportData.trim() === '') {
      showMessage('Invalid report data format', 'error');
      return;
    }
    
    // Check if it's a valid URL (data URL, http/https URL, relative URL, or blob URL)
    const isDataUrl = reportData.startsWith('data:');
    const isHttpUrl = reportData.startsWith('http://') || reportData.startsWith('https://');
    const isRelativeUrl = reportData.startsWith('/') || reportData.startsWith('./');
    const isBlobUrl = reportData.startsWith('blob:');
    
    if (!isDataUrl && !isHttpUrl && !isRelativeUrl && !isBlobUrl) {
      showMessage('Invalid report format. Cannot save this file.', 'error');
      return;
    }
    
    // Generate proper file name
    const equipmentName = (report.equipmentName || 'Equipment').replace(/\s+/g, '_');
    const assetId = report.assetId || 'Unknown';
    const serialNumber = report.serialNumber || 'SN_Unknown';
    const reportType = report.reportType === 'pm' ? 'PM' : 'CAL';
    const doneDate = report.doneDate || report.pmDoneDate || report.calibrationDoneDate || '';
    
    // Format date as DDMMYYYY
    let dateStr = '';
    // Convert to string if it's a Date object or other type
    const doneDateStr = doneDate ? String(doneDate) : '';
    if (doneDateStr && doneDateStr !== 'NA' && doneDateStr !== 'NR') {
      const dateMatch = doneDateStr.match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);
      if (dateMatch) {
        const day = dateMatch[1];
        const monthMap = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
          'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
          'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        const month = monthMap[dateMatch[2]] || '01';
        const year = dateMatch[3];
        dateStr = `${day}${month}${year}`;
      }
    }
    
    // Detect the correct file extension from mime type or filename
    let fileExt = 'jpg';
    if (isDataUrl) {
      const mimeMatch = reportData.match(/^data:([^;]+);/);
      if (mimeMatch) {
        const mime = mimeMatch[1];
        if (mime === 'application/pdf') fileExt = 'pdf';
        else if (mime === 'image/png') fileExt = 'png';
        else if (mime === 'image/gif') fileExt = 'gif';
        else fileExt = 'jpg';
      }
    } else {
      const fn = report.pmReportFileName || report.calReportFileName || '';
      const ext = fn.split('.').pop().toLowerCase();
      if (['pdf','png','jpg','jpeg','gif'].includes(ext)) fileExt = ext === 'jpeg' ? 'jpg' : ext;
    }

    const fileName = dateStr 
      ? `${equipmentName}_${assetId}_${serialNumber}_${reportType}_${dateStr}.${fileExt}`
      : `${equipmentName}_${assetId}_${serialNumber}_${reportType}.${fileExt}`;
    
    try {
      // Method 1: Try using Blob for better compatibility
      if (isDataUrl) {
        // Convert data URL to Blob
        const byteString = atob(reportData.split(',')[1]);
        const mimeString = reportData.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const blobUrl = URL.createObjectURL(blob);
        
        // Create download link with Blob URL
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        link.dispatchEvent(clickEvent);
        
        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);
        showMessage(`Report saved: ${fileName}`, 'success');
      } else {
        // For HTTP URLs, use direct download
        const link = document.createElement('a');
        link.href = reportData;
        link.download = fileName;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        showMessage(`Report saved: ${fileName}`, 'success');
      }
    } catch (err) {
      console.error('Error saving report:', err);
      
      // Fallback method: Try simple download
      try {
        const link = document.createElement('a');
        link.href = reportData;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showMessage(`Report saved: ${fileName}`, 'success');
      } catch (fallbackErr) {
        showMessage('Failed to save report. Please try using Preview and right-click to save.', 'error');
      }
    }
  };

  // Print the full filtered records table
  const handlePrintTable = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showMessage('Popup blocked! Please allow popups for this site.', 'error');
      return;
    }
    const rows = filteredReports.map(report => {
      const hasReport = !!(report.pmReportData || report.calReportData);
      return `<tr>
        <td>${report.assetId || '-'}</td>
        <td>${report.equipmentName || '-'}</td>
        <td>${report.serialNumber || '-'}</td>
        <td>${report.reportType === 'pm' ? 'PM' : 'Calibration'}</td>
        <td>${formatDateForDisplay(report.doneDate)}</td>
        <td>${formatDateForDisplay(report.dueDate)}</td>
        <td>${report.status || '-'}</td>
        <td>${hasReport ? '\u2705 Uploaded' : '\u2014'}</td>
      </tr>`;
    }).join('');
    printWindow.document.write(
      '<html><head><meta charset="utf-8"><title>PM &amp; Calibration Records</title>' +
      '<style>body{font-family:\'Segoe UI\',Arial,sans-serif;padding:20px;}' +
      'h1{color:#1a3c6c;font-size:20px;margin-bottom:4px;}' +
      'p{color:#64748b;font-size:13px;margin:0 0 16px;}' +
      'table{width:100%;border-collapse:collapse;font-size:13px;}' +
      'th{background:#1a3c6c;color:white;padding:8px 10px;text-align:left;}' +
      'td{padding:7px 10px;border-bottom:1px solid #e2e8f0;}' +
      'tr:nth-child(even){background:#f8fafc;}' +
      '@media print{.no-print{display:none;}}</style></head>' +
      '<body><h1>\uD83D\uDCCB PM &amp; Calibration Records</h1>' +
      '<p>Printed on: ' + new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ' &nbsp;&nbsp; Total records: ' + filteredReports.length + '</p>' +
      '<table><thead><tr>' +
      '<th>Asset ID</th><th>Equipment</th><th>Serial No</th><th>Type</th>' +
      '<th>Done Date</th><th>Due Date</th><th>Status</th><th>Report</th>' +
      '</tr></thead><tbody>' + rows + '</tbody></table>' +
      '<div class="no-print" style="text-align:center;margin-top:20px;">' +
      '<button onclick="window.print()" style="padding:10px 24px;background:#1a3c6c;color:white;border:none;border-radius:6px;cursor:pointer;font-size:14px;">\uD83D\uDDA8\uFE0F Print</button>' +
      '</div>' +
      '<script>window.onload=function(){window.print();}<\/script>' +
      '</body></html>'
    );
    printWindow.document.close();
  };

  // Handle view report - opens comprehensive report viewer with preview, print, save, history
  const handleViewReport = (report) => {
    const rawReportData = report.reportType === 'pm' ? report.pmReportData : report.calReportData;
    const reportData = normalizeReportPayload(rawReportData, getReportFileName(report));
    if (reportData) {
      // Check if reportData is a valid data URL or URL
      if (typeof reportData !== 'string' || reportData.trim() === '') {
        showMessage('Invalid report data', 'error');
        return;
      }
      
      // Validate that it's a proper data URL or valid URL (supports images and PDFs)
      const trimmedData = reportData.trim();
      const isDataUrl = trimmedData.startsWith('data:');
      const isHttpUrl = trimmedData.startsWith('http://') || trimmedData.startsWith('https://');
      const isRelativeUrl = trimmedData.startsWith('/') || trimmedData.startsWith('./');
      const isBlobUrl = trimmedData.startsWith('blob:');
      
      // Check if it's a base64 string (without data: prefix)
      const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(trimmedData) && trimmedData.length > 100;
      
      // Check if it looks like a file path or filename
      const isFilePath = trimmedData.includes('.') && (trimmedData.includes('/') || trimmedData.includes('\\'));
      
      if (!isDataUrl && !isHttpUrl && !isRelativeUrl && !isBlobUrl && !isBase64 && !isFilePath) {
        showMessage('Invalid report format. Report must be a valid file.', 'error');
        console.error('Report data validation failed:', {
          length: trimmedData.length,
          startsWith: trimmedData.substring(0, 50),
          isDataUrl,
          isHttpUrl,
          isBase64,
          isFilePath
        });
        return;
      }
      
      // If it's base64 without data prefix, add the prefix based on file extension
      let processedReportData = trimmedData;
      if (isBase64 && !isDataUrl) {
        // Try to detect file type from filename
        const fileName = report.pmReportFileName || report.calReportFileName || '';
        const ext = fileName.split('.').pop().toLowerCase();
        let mimeType = 'image/jpeg'; // default
        if (ext === 'png') mimeType = 'image/png';
        else if (ext === 'gif') mimeType = 'image/gif';
        else if (ext === 'pdf') mimeType = 'application/pdf';
        else if (ext === 'jpg' || ext === 'jpeg') mimeType = 'image/jpeg';
        
        processedReportData = `data:${mimeType};base64,${trimmedData}`;
        console.log('Converted base64 to data URL with mime type:', mimeType);
      }
      
      // Generate proper file name
      const equipmentName = (report.equipmentName || 'Equipment').replace(/\s+/g, '_');
      const assetId = report.assetId || 'Unknown';
      const serialNumber = report.serialNumber || 'SN_Unknown';
      const reportType = report.reportType === 'pm' ? 'PM' : 'CAL';
      const doneDate = report.doneDate || report.pmDoneDate || report.calibrationDoneDate || '';
      
      // Format date as DDMMYYYY
      let dateStr = '';
      // Convert to string if it's a Date object or other type
      const doneDateStr = doneDate ? String(doneDate) : '';
      if (doneDateStr && doneDateStr !== 'NA' && doneDateStr !== 'NR') {
        const dateMatch = doneDateStr.match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);
        if (dateMatch) {
          const day = dateMatch[1];
          const monthMap = {
            'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
            'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
            'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
          };
          const month = monthMap[dateMatch[2]] || '01';
          const year = dateMatch[3];
          dateStr = `${day}${month}${year}`;
        }
      }
      
      // Detect the correct file extension from mime type or filename
      let fileExt = 'jpg';
      if (processedReportData.startsWith('data:')) {
        const mimeMatch = processedReportData.match(/^data:([^;]+);/);
        if (mimeMatch) {
          const mime = mimeMatch[1];
          if (mime === 'application/pdf') fileExt = 'pdf';
          else if (mime === 'image/png') fileExt = 'png';
          else if (mime === 'image/gif') fileExt = 'gif';
          else fileExt = 'jpg';
        }
      } else {
        const fn = report.pmReportFileName || report.calReportFileName || '';
        const ext = fn.split('.').pop().toLowerCase();
        if (['pdf','png','jpg','jpeg','gif'].includes(ext)) fileExt = ext === 'jpeg' ? 'jpg' : ext;
      }

      const fileName = dateStr 
        ? `${equipmentName}_${assetId}_${serialNumber}_${reportType}_${dateStr}.${fileExt}`
        : `${equipmentName}_${assetId}_${serialNumber}_${reportType}.${fileExt}`;
      
      // Get report history from the asset's embedded history arrays
      const reportHistory = report.reportType === 'pm'
        ? (report.pmReportHistory || [])
        : (report.calReportHistory || []);
      
      // Create a blob URL from the report data for better compatibility
      let fileUrl = processedReportData;
      let fileMimeType = '';
      if (isDataUrl || isBase64) {
        // Convert data URL to blob URL for better browser compatibility
        try {
          const byteString = atob(processedReportData.split(',')[1]);
          const mimeString = processedReportData.split(',')[0].split(':')[1].split(';')[0];
          fileMimeType = mimeString;
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: mimeString });
          fileUrl = URL.createObjectURL(blob);
        } catch (e) {
          console.error('Error converting data URL to blob:', e);
          fileUrl = processedReportData; // Fallback to original data URL
        }
      }
      
      // Detect if file is PDF based on mime type or file extension
      const isPdf = fileMimeType.includes('pdf') || 
                    processedReportData.toLowerCase().includes('pdf') ||
                    (report.pmReportFileName || report.calReportFileName || '').toLowerCase().endsWith('.pdf');
      
      // First, try to open the window immediately (before any async operations)
      // This is crucial to avoid popup blockers
      const newWindow = window.open('about:blank', '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup was blocked - show message and use fallback
        showMessage('Popup blocked! Please allow popups for this site to view reports.', 'error');
        
        // Fallback: Try to open the file directly in a new tab
        const link = document.createElement('a');
        link.href = fileUrl;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      try {
        newWindow.document.write(`
          <html>
            <head>
              <meta charset="utf-8"><title>Report Viewer - ${report.equipmentName}</title>
              <style>
                * { box-sizing: border-box; }
                body { margin: 0; padding: 0; font-family: 'Segoe UI', Arial, sans-serif; background: #f0f2f5; }
                .header { background: linear-gradient(135deg, #1a3c6c, #2d5a9d); color: white; padding: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                .header h1 { margin: 0; font-size: 24px; }
                .header p { margin: 5px 0 0 0; opacity: 0.9; }
                .toolbar { background: white; padding: 15px 20px; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; border-bottom: 1px solid #e0e0e0; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
                .btn { padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
                .btn-primary { background: #4361ee; color: white; }
                .btn-primary:hover { background: #3651d4; }
                .btn-success { background: #06d6a0; color: white; }
                .btn-success:hover { background: #05b589; }
                .btn-info { background: #0ea5e9; color: white; }
                .btn-info:hover { background: #0284c7; }
                .btn-warning { background: #f59e0b; color: white; }
                .btn-warning:hover { background: #d97706; }
                .container { max-width: 1400px; margin: 0 auto; padding: 20px; display: grid; grid-template-columns: 1fr 350px; gap: 20px; }
                .main-content { background: white; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); overflow: hidden; }
                .sidebar { display: flex; flex-direction: column; gap: 20px; }
                .card { background: white; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); overflow: hidden; }
                .card-header { background: #f8fafc; padding: 15px 20px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #1e293b; }
                .card-body { padding: 20px; }
                .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
                .info-item { display: flex; flex-direction: column; }
                .info-label { font-size: 12px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
                .info-value { font-size: 14px; color: #1e293b; font-weight: 500; }
                .image-container { padding: 20px; text-align: center; background: #f8fafc; min-height: 500px; display: flex; align-items: center; justify-content: center; }
                .image-container img { max-width: 100%; max-height: 800px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
                .pdf-container { width: 100%; height: 800px; border: none; }
                .history-list { max-height: 400px; overflow-y: auto; }
                .history-item { padding: 12px; border-bottom: 1px solid #e2e8f0; cursor: pointer; transition: background 0.2s; }
                .history-item:hover { background: #f1f5f9; }
                .history-item:last-child { border-bottom: none; }
                .history-date { font-size: 13px; color: #64748b; }
                .history-file { font-size: 14px; color: #1e293b; font-weight: 500; margin-top: 4px; }
                .badge { display: inline-block; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; }
                .badge-pm { background: #dbeafe; color: #1e40af; }
                .badge-cal { background: #fce7f3; color: #9d174d; }
                @media print {
                  .header, .toolbar, .sidebar { display: none !important; }
                  .container { grid-template-columns: 1fr; padding: 0; }
                  .main-content { box-shadow: none; }
                  .image-container { background: white; }
                }
                @media (max-width: 1024px) {
                  .container { grid-template-columns: 1fr; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>&#x1F4C4; ${report.reportType === 'pm' ? 'PM Report' : 'Calibration Report'}</h1>
                <p>${report.equipmentName} | Asset ID: ${assetId}</p>
              </div>
              
              <div class="toolbar">
                <button class="btn btn-primary" onclick="triggerPrint()">&#x1F5A8;&#xFE0F; Print
                </button>
                <button class="btn btn-success" onclick="downloadReport()">&#x1F4BE; Save / Download
                </button>
                <button class="btn btn-info" onclick="toggleFullscreen()">&#x1F441;&#xFE0F; Preview
                </button>
                <div style="margin-left: auto; display: flex; align-items: center; gap: 10px;">
                  <span class="badge badge-${report.reportType === 'pm' ? 'pm' : 'cal'}">
                    ${report.reportType === 'pm' ? 'PM' : 'Calibration'}
                  </span>
                  <span style="color: #64748b; font-size: 14px;">${fileName}</span>
                </div>
              </div>
              
              <div class="container">
                <div class="main-content">
                  <div class="image-container" id="imageContainer">
                    ${isPdf ? 
                      `<embed src="${fileUrl}" type="application/pdf" class="pdf-container" />` :
                      `<img src="${fileUrl}" alt="Report" id="reportImage" onload="console.log('Image loaded successfully')" onerror="console.error('Image failed to load'); this.src='${reportData}';" />`
                    }
                  </div>
                </div>
                
                <div class="sidebar">
                  <div class="card">
                    <div class="card-header">&#x1F4CB; Report Information</div>
                    <div class="card-body">
                      <div class="info-grid">
                        <div class="info-item">
                          <span class="info-label">Equipment</span>
                          <span class="info-value">${report.equipmentName}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Asset ID</span>
                          <span class="info-value">${assetId}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Serial Number</span>
                          <span class="info-value">${serialNumber}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Report Type</span>
                          <span class="info-value">${report.reportType === 'pm' ? 'PM' : 'Calibration'}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Done Date</span>
                          <span class="info-value">${report.doneDate || report.pmDoneDate || report.calibrationDoneDate || 'N/A'}</span>
                        </div>
                        <div class="info-item">
                          <span class="info-label">Due Date</span>
                          <span class="info-value">${report.dueDate || report.pmDueDate || report.calibrationDueDate || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="card"><div class="card-header">&#x1F4DC; History (${reportHistory.length} versions)</div>
                    <div class="card-body">
                      <div class="history-list">
                        ${reportHistory.length > 0 ? reportHistory.map((hist, idx) => `
                          <div class="history-item" onclick="loadHistoryVersion(${idx})">
                            <div class="history-date">${hist.uploadDate || hist.date || 'Unknown date'}</div>
                            <div class="history-file">${hist.fileName || 'Report ' + (idx + 1)}</div>
                          </div>
                        `).join('') : `
                          <div style="text-align: center; color: #64748b; padding: 20px;">
                            No previous versions found
                          </div>
                        `}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <script>
                // Download report function
                function downloadReport() {
                  const link = document.createElement('a');
                  link.href = '${reportData}';
                  link.download = '${fileName}';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }

                function triggerPrint() {
                  try {
                    window.focus();
                    window.print();
                  } catch (_error) {
                    window.print();
                  }
                }
                function toggleFullscreen() {
                  const target = document.querySelector('.pdf-container') || document.getElementById('reportImage') || document.getElementById('imageContainer');
                  if (!target) return;
                  const requestFullscreen = target.requestFullscreen || target.webkitRequestFullscreen || target.msRequestFullscreen;
                  if (requestFullscreen) {
                    requestFullscreen.call(target);
                  }
                }
                function loadHistoryVersion(index) {
                  alert('Loading version ' + (index + 1) + '...');
                }
              </script>
            </body>
          </html>
        `);
        newWindow.document.close();
      } catch (err) {
        console.error('Error opening report viewer:', err);
        showMessage('Error displaying report. Trying fallback method...', 'error');
        
        // Fallback: Try to open the image directly
        const link = document.createElement('a');
        link.href = reportData;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      showMessage(rawReportData ? 'Report preview is unavailable due to invalid or storage-limited data.' : 'No report uploaded yet', 'error');
    }
  };

  // Handle view history - opens history modal for a report
  const handleViewHistory = (report) => {
    const assetId = report.assetId || 'Unknown';
    const reportType = report.reportType;
    // Get report history from the asset's embedded history arrays
    const reportHistory = reportType === 'pm'
      ? (report.pmReportHistory || [])
      : (report.calReportHistory || []);
    
    // Also get current report data
    const currentReportData = reportType === 'pm' ? report.pmReportData : report.calReportData;
    const currentFileName = reportType === 'pm' ? report.pmReportFileName : report.calReportFileName;
    const currentUploadDate = reportType === 'pm' ? report.pmReportUploadDate : report.calReportUploadDate;
    // Build full history list including current version
    const fullHistory = [];
    if (currentReportData) {
      fullHistory.push({
        version: 'Current',
        fileName: currentFileName || `${report.equipmentName}_${assetId}_${reportType}_Current.jpg`,
        uploadDate: currentUploadDate || new Date().toISOString().split('T')[0],
        data: currentReportData,
        isCurrent: true
      });
    }
    
    // Add historical versions
    reportHistory.forEach((hist, idx) => {
      fullHistory.push({
        version: `Version ${reportHistory.length - idx}`,
        fileName: hist.fileName || `${report.equipmentName}_${assetId}_${reportType}_v${reportHistory.length - idx}.jpg`,
        uploadDate: hist.uploadDate || hist.date || 'Unknown date',
        data: hist.data || hist.reportData,
        isCurrent: false
      });
    });
    
    if (fullHistory.length === 0) {
      showMessage('No report history found for this equipment', 'info');
      return;
    }
    // Open history viewer
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <meta charset="utf-8">
            <title>Report History - ${report.equipmentName}</title>
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; padding: 0; font-family: 'Segoe UI', Arial, sans-serif; background: #f0f2f5; }
              .header { background: linear-gradient(135deg, #1a3c6c, #2d5a9d); color: white; padding: 20px; }
              .header h1 { margin: 0; font-size: 24px; }
              .header p { margin: 5px 0 0 0; opacity: 0.9; }
              .container { max-width: 1200px; margin: 0 auto; padding: 20px; }
              .history-grid { display: grid; gap: 15px; }
              .history-card { background: white; border-radius: 12px; padding: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); display: flex; align-items: center; gap: 20px; transition: transform 0.2s, box-shadow 0.2s; }
              .history-card:hover { transform: translateY(-2px); box-shadow: 0 4px 20px rgba(0,0,0,0.12); }
              .history-card.current { border-left: 4px solid #06d6a0; }
              .history-card.old { border-left: 4px solid #0ea5e9; }
              .version-badge { min-width: 100px; text-align: center; }
              .version-badge span { display: inline-block; padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; }
              .version-badge .current { background: #d1fae5; color: #065f46; }
              .version-badge .old { background: #e0f2fe; color: #0369a1; }
              .file-info { flex: 1; }
              .file-name { font-size: 16px; font-weight: 600; color: #1e293b; margin-bottom: 5px; }
              .file-date { font-size: 13px; color: #64748b; }
              .actions { display: flex; gap: 10px; }
              .btn { padding: 8px 16px; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s; }
              .btn-primary { background: #4361ee; color: white; }
              .btn-primary:hover { background: #3651d4; }
              .btn-success { background: #06d6a0; color: white; }
              .btn-success:hover { background: #05b589; }
              .empty-state { text-align: center; padding: 60px 20px; color: #64748b; }
              .stats { background: white; border-radius: 12px; padding: 20px; margin-bottom: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); display: flex; gap: 30px; }
              .stat { text-align: center; }
              .stat-value { font-size: 28px; font-weight: 700; color: #1e293b; }
              .stat-label { font-size: 13px; color: #64748b; margin-top: 4px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>📜 Report History</h1>
              <p>${report.equipmentName} | Asset ID: ${assetId} | Type: ${reportType === 'pm' ? 'PM' : 'Calibration'}</p>
            </div>
            
            <div class="container">
              <div class="stats">
                <div class="stat">
                  <div class="stat-value">${fullHistory.length}</div>
                  <div class="stat-label">Total Versions</div>
                </div>
                <div class="stat">
                  <div class="stat-value">${fullHistory.filter(h => h.isCurrent).length}</div>
                  <div class="stat-label">Current</div>
                </div>
                <div class="stat">
                  <div class="stat-value">${fullHistory.filter(h => !h.isCurrent).length}</div>
                  <div class="stat-label">Archived</div>
                </div>
              </div>
              
              <div class="history-grid">
                ${fullHistory.map((item, idx) => `
                  <div class="history-card ${item.isCurrent ? 'current' : 'old'}">
                    <div class="version-badge">
                      <span class="${item.isCurrent ? 'current' : 'old'}">${item.version}</span>
                    </div>
                    <div class="file-info">
                      <div class="file-name">${item.fileName}</div>
                      <div class="file-date">&#x1F4C5; ${item.uploadDate}</div>
                    </div>
                    <div class="actions">
                      <button class="btn btn-primary" onclick="viewVersion('${idx}')">&#x1F441;&#xFE0F; View
                      </button>
                      <button class="btn btn-success" onclick="downloadVersion('${idx}')">
                        &#x1F4BE; Download
                      </button>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            
            <script>
              const historyData = ${JSON.stringify(fullHistory)};

              function viewVersion(index) {
                const item = historyData[index];
                if (!item || !item.data) return;

                const viewerWindow = window.open('', '_blank');
                if (!viewerWindow) return;

                const isPdf = item.data.startsWith('data:application/pdf') || item.data.toLowerCase().includes('.pdf');
                const reportContent = isPdf
                  ? '<embed src="' + item.data + '" type="application/pdf" style="width:100%; height:800px; border:1px solid #ddd; border-radius:4px;" />'
                  : '<img src="' + item.data + '" alt="Report" style="max-width:100%; border:1px solid #ddd; border-radius:4px;" />';

                const htmlContent = '<html>' +
                  '<head><meta charset="utf-8"><title>Viewing ' + item.fileName + '</title>' +
                  '<style>' +
                  'body{margin:0;padding:20px;font-family:Arial,sans-serif;background:#f5f5f5;text-align:center;}' +
                  '.container{max-width:1200px;margin:0 auto;background:white;padding:20px;border-radius:8px;}' +
                  'h1{color:#333;font-size:18px;}' +
                  '.info{margin-bottom:20px;padding:15px;background:#e3f2fd;border-radius:4px;text-align:left;}' +
                  '.btn{padding:10px 20px;margin:5px;border:none;border-radius:6px;cursor:pointer;font-size:14px;}' +
                  '.btn-primary{background:#4361ee;color:white;}' +
                  '.btn-success{background:#06d6a0;color:white;}' +
                  '</style></head>' +
                  '<body><div class="container">' +
                  '<h1>&#x1F4C4; ' + item.fileName + '</h1>' +
                  '<div class="info"><strong>Version:</strong> ' + item.version + '<br><strong>Upload Date:</strong> ' + item.uploadDate + '</div>' +
                  reportContent +
                  '<div style="margin-top:20px;">' +
                  '<button class="btn btn-primary" onclick="triggerPrint()">&#x1F5A8;&#xFE0F; Print</button>' +
                  '<button class="btn btn-success" onclick="downloadReport()">&#x1F4BE; Download</button>' +
                  '</div></div>' +
                  '<script>function triggerPrint(){try{window.focus();window.print();}catch(_error){window.print();}}function downloadReport(){var link=document.createElement("a");link.href="' + item.data + '";link.download="' + item.fileName + '";document.body.appendChild(link);link.click();document.body.removeChild(link);}<' + '/script>' +
                  '</body></html>';

                viewerWindow.document.write(htmlContent);
                viewerWindow.document.close();
              }

              function downloadVersion(index) {
                const item = historyData[index];
                if (item && item.data) {
                  const link = document.createElement('a');
                  link.href = item.data;
                  link.download = item.fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }
            </script>
          </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      showMessage('Popup blocked! Please allow popups for this site.', 'error');
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('done')) return 'success';
    if (statusLower.includes('pending') || statusLower.includes('due')) return 'warning';
    if (statusLower.includes('overdue')) return 'danger';
    return 'info';
  };

  return (
    <div className="pm-cal-reports-container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{"\u{1F4C1}"} PM & Calibration Reports</h2>
        </div>

        {currentUser && (
          <div className="user-badge">{"\u{1F464}"} Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '\u2705' : message.type === 'error' ? '\u274C' : '\u2139\uFE0F'} {message.text}
          </div>
        )}

        <div className="reports-sections">
          {/* Filter Section */}
          <div className="reports-section filter-section">
            <h3>{"\u{1F50D}"} Filter Records</h3>
            <div className="filter-controls">
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label>Asset ID</label>
                    <input
                      type="text"
                      value={filterAssetId}
                      onChange={(e) => setFilterAssetId(e.target.value)}
                      placeholder="Filter by asset ID"
                    />
                  </div>
                </div>

                <div className="form-col">
                  <div className="form-group">
                    <label>Equipment Name</label>
                    <input
                      type="text"
                      value={filterEquipment}
                      onChange={(e) => setFilterEquipment(e.target.value)}
                      placeholder="Filter by equipment name"
                    />
                  </div>
                </div>

                <div className="form-col">
                  <div className="form-group">
                    <label>Report Type</label>
                    <select
                      value={filterReportType}
                      onChange={(e) => setFilterReportType(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="pm">PM Only</option>
                      <option value="calibration">Calibration Only</option>
                    </select>
                  </div>
                </div>

                <div className="form-col">
                  <div className="form-group">
                    <label>Serial Number</label>
                    <input
                      type="text"
                      value={filterSerialNumber}
                      onChange={(e) => setFilterSerialNumber(e.target.value)}
                      placeholder="Filter by serial number"
                    />
                  </div>
                </div>

                <div className="form-col">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reports List */}
          <div className="reports-section list-section">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'10px',marginBottom:'12px'}}>
              <h3 style={{margin:0}}>{"\u{1F4CB}"} PM & Calibration Records ({filteredReports.length})</h3>
              <button type="button" className="btn btn-info" onClick={handlePrintTable} title="Print all visible records">
                🖨️ Print Records
              </button>
            </div>
            
            {loading ? (
              <div className="loading-message">{"\u23F3"} Loading records...</div>
            ) : filteredReports.length === 0 ? (
              <div className="empty-message">{"\u{1F4ED}"} No PM/Calibration records found</div>
            ) : (
              <div className="reports-table-container">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>Equipment</th>
                      <th>Serial Number</th>
                      <th>Type</th>
                      <th>Done Date</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Report</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.map(report => (
                      <tr key={report.id}>
                        <td>{report.assetId || '-'}</td>
                        <td>{report.equipmentName}</td>
                        <td>{report.serialNumber}</td>
                        <td>
                          <span className={`badge ${report.reportType}`}>
                            {report.reportType === 'pm' ? 'PM' : 'Calibration'}
                          </span>
                        </td>
                        <td>{formatDateForDisplay(report.doneDate)}</td>
                        <td>{formatDateForDisplay(report.dueDate)}</td>
                        <td><span className={`badge ${getStatusBadgeClass(report.status)}`}>{report.status || 'Unknown'}</span></td>
                        <td style={{textAlign:'center'}}>
                          {(report.pmReportData || report.calReportData)
                            ? <span title="Report uploaded" style={{color:'#06d6a0',fontWeight:600}}>✅</span>
                            : <span style={{color:'#94a3b8'}}>—</span>}
                        </td>
                        <td className="actions-cell">
                          {/* Eye Button with Dropdown Menu */}
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              type="button"
                              className="btn btn-info btn-small eye-main-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Toggle dropdown for this report
                                const dropdown = document.getElementById(`dropdown-${report.id}`);
                                if (dropdown) {
                                  const isVisible = dropdown.style.display === 'block';
                                  // Hide all other dropdowns first
                                  document.querySelectorAll('.report-action-dropdown').forEach(d => {
                                    d.style.display = 'none';
                                  });
                                  dropdown.style.display = isVisible ? 'none' : 'block';
                                }
                              }}
                              title="View Options"
                            >{"\u{1F441}\uFE0F"} View
                            </button>
                            {/* Dropdown Menu */}
                            <div
                              id={`dropdown-${report.id}`}
                              className="report-action-dropdown"
                              style={{
                                display: 'none',
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                backgroundColor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.375rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                zIndex: 1000,
                                minWidth: '150px',
                                marginTop: '2px'
                              }}
                            >
                              <button
                                type="button"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const dropdown = document.getElementById(`dropdown-${report.id}`);
                                  if (dropdown) dropdown.style.display = 'none';
                                  // Use setTimeout to ensure dropdown closes before opening report
                                  setTimeout(() => {
                                    handleViewReport(report);
                                  }, 10);
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >{"\u{1F441}\uFE0F"} Preview
                              </button>
                              <button
                                type="button"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const dropdown = document.getElementById(`dropdown-${report.id}`);
                                  if (dropdown) dropdown.style.display = 'none';
                                  setTimeout(() => {
                                    handlePrintReport(report);
                                  }, 10);
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >{"\u{1F5A8}\uFE0F"} Print
                              </button>
                              <button
                                type="button"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  borderBottom: '1px solid #f1f5f9'
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const dropdown = document.getElementById(`dropdown-${report.id}`);
                                  if (dropdown) dropdown.style.display = 'none';
                                  setTimeout(() => {
                                    handleSaveReport(report);
                                  }, 10);
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >{"\u{1F4BE}"} Save
                              </button>
                              <button
                                type="button"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem'
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const dropdown = document.getElementById(`dropdown-${report.id}`);
                                  if (dropdown) dropdown.style.display = 'none';
                                  setTimeout(() => {
                                    handleViewHistory(report);
                                  }, 10);
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >{"\u{1F4DC}"} History
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Summary Stats */}
          <div className="reports-section stats-section">
            <h3>{"\u{1F4CA}"} Summary</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <h5>Total PM Records</h5>
                <div className="stat-value">
                  {allReports.filter(r => r.reportType === 'pm').length}
                </div>
              </div>
              <div className="stat-card">
                <h5>Total Calibration Records</h5>
                <div className="stat-value">
                  {allReports.filter(r => r.reportType === 'calibration').length}
                </div>
              </div>
              <div className="stat-card">
                <h5>Total Equipment Types</h5>
                <div className="stat-value">{uniqueEquipmentNames.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PMCalibrationReports;


